// Phase 9 — Operator console shell.
//
// Layout (desktop):
//
//   ┌──────────────────────────────────────────────────────────────────┐
//   │ sticky 60px header — wordmark | 6-group nav | search btn + user  │
//   ├──────────────────────────────────────────────────────────────────┤
//   │ <Breadcrumbs/>                                                   │
//   ├──────────────────────────────────────────────────────────────────┤
//   │                                                                  │
//   │ <Outlet/> — every authenticated page                             │
//   │                                                                  │
//   └──────────────────────────────────────────────────────────────────┘
//
// Layout (mobile, < 768px):
//   header collapses to a hamburger; tapping it opens a drawer that
//   stacks the 6 groups vertically with expandable sub-items.
//
// The AuthGate / TenantGate / SubscriptionGate semantics live *outside*
// this component (App.js wraps the shell in <RequireAuth>). AppShell
// assumes an authenticated user and reads role via useUserRole() to
// hide the ADMIN group for non-admins.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Link,
    NavLink,
    Outlet,
    useLocation,
    useNavigate,
} from "react-router-dom";
import {
    Activity,
    BookOpen,
    ChevronDown,
    LogOut,
    Menu,
    Search,
    ShieldCheck,
    UserCog,
    X,
} from "lucide-react";
import { health as fetchHealth } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import useUserRole from "@/hooks/useUserRole";
import Logo from "@/components/Logo";
import Breadcrumbs from "@/components/Breadcrumbs";
import CommandPalette from "@/components/CommandPalette";
import { GROUPS, routesForGroup } from "@/lib/routeMeta";

const APP_VERSION = "v0.3.0";
const DOCS_URL = "/docs";

function selfcheckSeverity(selfcheck) {
    if (!selfcheck) return "amber";
    const issues = [];
    if (selfcheck.jsbsim && !selfcheck.jsbsim.installed) issues.push("jsbsim");
    if (selfcheck.mongo && !selfcheck.mongo.connected) issues.push("mongo");
    if (selfcheck.presets_root && !selfcheck.presets_root.exists) issues.push("presets");
    if (issues.includes("mongo") || issues.includes("presets")) return "red";
    if (issues.length) return "amber";
    return "green";
}

function StatusDot({ severity }) {
    const cls =
        severity === "green"
            ? "bg-ux-success"
            : severity === "amber"
              ? "bg-ux-warning"
              : "bg-ux-danger";
    return (
        <span
            className={`inline-block w-2 h-2 rounded-full ${cls}`}
            aria-hidden
            data-testid="header-status-dot"
        />
    );
}

function HealthBadge({ data }) {
    if (!data) {
        return (
            <span className="text-[11px] text-ux-text-3 font-mono" data-testid="header-health-pill">
                …
            </span>
        );
    }
    return (
        <span
            className="hidden md:inline-flex items-center gap-2 text-[11px] tabular-nums text-ux-text-2 font-mono"
            data-testid="header-health-pill"
        >
            <Activity className="w-3.5 h-3.5 text-ux-accent" />
            <span>
                <span className="text-ux-text-3">run</span>{" "}
                <span className="text-ux-text-1" data-testid="header-running-count">
                    {data.running_runs ?? 0}
                </span>
            </span>
            <span className="text-ux-text-3">/</span>
            <span>
                <span className="text-ux-text-3">q</span>{" "}
                <span className="text-ux-text-1" data-testid="header-queued-count">
                    {data.queued_runs ?? 0}
                </span>
            </span>
        </span>
    );
}

// ---------------------------------------------------------------------
// GroupDropdown — one per NAV group. Opens on hover OR click; closes on
// outside click / Escape / route change.
// ---------------------------------------------------------------------
function GroupDropdown({ group, currentPath, onNavigate }) {
    const items = routesForGroup(group.id);
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const closeTimer = useRef(null);

    const groupActive = items.some(
        (r) => currentPath === r.path || currentPath.startsWith(r.path + "/"),
    );

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const h = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        window.addEventListener("mousedown", h);
        return () => window.removeEventListener("mousedown", h);
    }, [open]);

    // Close on Escape.
    useEffect(() => {
        if (!open) return;
        const h = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", h);
        return () => window.removeEventListener("keydown", h);
    }, [open]);

    const openWithHover = () => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
        setOpen(true);
    };
    const scheduleClose = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        closeTimer.current = setTimeout(() => setOpen(false), 140);
    };

    return (
        <div
            ref={wrapRef}
            className="relative"
            onMouseEnter={openWithHover}
            onMouseLeave={scheduleClose}
            data-testid={`nav-group-${group.id}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={[
                    "inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[12px] font-medium tracking-[0.12em] uppercase transition-colors",
                    groupActive
                        ? "text-ux-text-1 bg-ux-bg-2"
                        : "text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2",
                ].join(" ")}
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid={`nav-group-button-${group.id}`}
            >
                {group.label}
                <ChevronDown className="w-3 h-3 opacity-70" aria-hidden />
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute left-0 top-full mt-1 min-w-[280px] rounded-lg border border-ux-border-1 bg-ux-bg-1 shadow-2xl py-1 z-50"
                    data-testid={`nav-group-panel-${group.id}`}
                >
                    <div className="px-3 py-2 text-[10px] uppercase tracking-[0.18em] text-ux-text-3 border-b border-ux-border-1">
                        {group.description}
                    </div>
                    {items.map((r) => (
                        <NavLink
                            key={r.id}
                            to={r.path}
                            end
                            onClick={() => {
                                setOpen(false);
                                onNavigate?.();
                            }}
                            className={({ isActive }) =>
                                [
                                    "flex flex-col gap-0.5 px-3 py-2 mx-1 my-0.5 rounded-md text-[13px] transition-colors",
                                    isActive
                                        ? "bg-ux-bg-2 text-ux-text-1"
                                        : "text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2",
                                ].join(" ")
                            }
                            data-testid={`nav-item-${r.id}`}
                        >
                            <span className="font-medium">{r.label}</span>
                            <span className="text-[11px] text-ux-text-3">{r.description}</span>
                        </NavLink>
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Role badge shown in the user cluster.
// ---------------------------------------------------------------------
function RoleBadge({ role }) {
    const isAdmin = role === "admin" || role === "superadmin";
    return (
        <span
            data-testid="header-role-badge"
            className={[
                "inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded border",
                isAdmin
                    ? "border-ux-warning/40 text-ux-warning bg-ux-warning-soft"
                    : "border-ux-border-2 text-ux-text-2 bg-ux-bg-2",
            ].join(" ")}
        >
            {isAdmin ? <ShieldCheck className="w-3 h-3" /> : <UserCog className="w-3 h-3" />}
            {role || "—"}
        </span>
    );
}

// ---------------------------------------------------------------------
// User cluster (right side of the header) — dropdown.
// ---------------------------------------------------------------------
function UserCluster({ user, onLogout }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    useEffect(() => {
        if (!open) return;
        const h = (e) => {
            if (!wrapRef.current?.contains(e.target)) setOpen(false);
        };
        window.addEventListener("mousedown", h);
        return () => window.removeEventListener("mousedown", h);
    }, [open]);
    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-ux-border-1 hover:bg-ux-bg-2 transition-colors"
                data-testid="header-user-menu-button"
            >
                <span
                    className="font-mono text-[12px] text-ux-text-1 truncate max-w-[140px]"
                    data-testid="header-username"
                >
                    {user?.username || "anon"}
                </span>
                <RoleBadge role={user?.role} />
                <ChevronDown className="w-3 h-3 text-ux-text-3" aria-hidden />
            </button>
            {open && (
                <div
                    className="absolute right-0 top-full mt-1 w-56 rounded-lg border border-ux-border-1 bg-ux-bg-1 shadow-2xl py-1 z-50"
                    role="menu"
                    data-testid="header-user-menu-panel"
                >
                    <Link
                        to="/account/change-password"
                        onClick={() => setOpen(false)}
                        className="block px-3 py-2 mx-1 rounded-md text-[13px] text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        data-testid="header-change-password-link"
                    >
                        Change password
                    </Link>
                    <Link
                        to="/help"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 mx-1 rounded-md text-[13px] text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        data-testid="header-user-guide-link"
                    >
                        <BookOpen className="w-3.5 h-3.5" />
                        User guide
                    </Link>
                    <a
                        href={DOCS_URL}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 mx-1 rounded-md text-[13px] text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        data-testid="docs-system-overview-link"
                    >
                        <BookOpen className="w-3.5 h-3.5" />
                        Public docs
                    </a>
                    <div className="border-t border-ux-border-1 my-1" />
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(false);
                            onLogout();
                        }}
                        className="w-full text-left flex items-center gap-2 px-3 py-2 mx-1 rounded-md text-[13px] text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        data-testid="header-logout-button"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        Logout
                    </button>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------
// Mobile drawer — stacked version of the 6-group nav.
// ---------------------------------------------------------------------
function MobileDrawer({ open, onClose, groups, onNavigate }) {
    if (!open) return null;
    return (
        <div className="md:hidden fixed inset-0 z-[60]">
            <button
                type="button"
                aria-label="Close navigation"
                onClick={onClose}
                className="absolute inset-0 bg-black/60 backdrop-blur-[1px]"
                data-testid="app-drawer-backdrop"
            />
            <aside
                className="absolute top-0 left-0 h-full w-[86%] max-w-[320px] bg-ux-bg-1 border-r border-ux-border-1 overflow-y-auto"
                data-testid="app-drawer"
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-ux-border-1">
                    <Logo variant="wordmark" size="sm" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                        aria-label="Close"
                        data-testid="app-drawer-close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <nav className="p-2">
                    {groups.map((g) => {
                        const items = routesForGroup(g.id);
                        return (
                            <div key={g.id} className="mb-2" data-testid={`drawer-group-${g.id}`}>
                                <div className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-[0.18em] text-ux-text-3">
                                    {g.label}
                                </div>
                                {items.map((r) => (
                                    <NavLink
                                        key={r.id}
                                        to={r.path}
                                        end
                                        onClick={() => {
                                            onNavigate?.();
                                            onClose();
                                        }}
                                        className={({ isActive }) =>
                                            [
                                                "block px-3 py-2 mx-1 my-0.5 rounded-md text-[13px] transition-colors",
                                                isActive
                                                    ? "bg-ux-bg-2 text-ux-text-1"
                                                    : "text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2",
                                            ].join(" ")
                                        }
                                        data-testid={`drawer-item-${r.id}`}
                                    >
                                        {r.label}
                                    </NavLink>
                                ))}
                            </div>
                        );
                    })}
                </nav>
            </aside>
        </div>
    );
}

// ---------------------------------------------------------------------
// Main shell.
// ---------------------------------------------------------------------
export default function AppShell() {
    const { logout, user } = useAuth();
    const { canSeeAdmin } = useUserRole();
    const navigate = useNavigate();
    const location = useLocation();
    const [hb, setHb] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [paletteOpen, setPaletteOpen] = useState(false);

    // Auto-close drawer on route change.
    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    // Health poll.
    useEffect(() => {
        let cancelled = false;
        let timer;
        async function poll() {
            try {
                const d = await fetchHealth();
                if (!cancelled) setHb(d);
            } catch {
                if (!cancelled) setHb({ status: "down" });
            } finally {
                timer = setTimeout(poll, 5000);
            }
        }
        poll();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, []);

    const visibleGroups = useMemo(
        () => GROUPS.filter((g) => !g.adminOnly || canSeeAdmin),
        [canSeeAdmin],
    );

    const handleLogout = useCallback(async () => {
        await logout();
        navigate("/login", { replace: true });
    }, [logout, navigate]);

    const severity = selfcheckSeverity(hb?.selfcheck);
    const platformCmd = useMemo(() => {
        if (typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform || "")) {
            return "⌘K";
        }
        return "Ctrl+K";
    }, []);

    return (
        <div
            className="min-h-screen bg-ux-bg-0 text-ux-text-1 flex flex-col"
            data-testid="app-shell"
        >
            {/* ─────────────── STICKY HEADER (60px) ─────────────── */}
            <header
                className="sticky top-0 z-40 h-[60px] border-b border-ux-border-1 bg-[color-mix(in_srgb,var(--ux-bg-1)_92%,black)] backdrop-blur-sm flex items-center px-3 sm:px-4 lg:px-6 gap-3"
                data-testid="app-header"
            >
                {/* Hamburger — mobile only */}
                <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2"
                    aria-label="Open navigation"
                    data-testid="app-drawer-open"
                >
                    <Menu className="w-5 h-5" />
                </button>

                {/* Wordmark — left */}
                <Link
                    to="/run/history"
                    className="inline-flex items-center gap-2 shrink-0"
                    data-testid="header-title"
                    aria-label="UAVSimNet — go to Run History"
                >
                    <StatusDot severity={severity} />
                    <Logo variant="wordmark" size="md" />
                </Link>

                {/* Primary nav — centre (desktop only) */}
                <nav
                    className="hidden md:flex items-center gap-0.5 mx-auto"
                    aria-label="Primary"
                    data-testid="primary-nav"
                >
                    {visibleGroups.map((g) => (
                        <GroupDropdown
                            key={g.id}
                            group={g}
                            currentPath={location.pathname}
                            onNavigate={() => setDrawerOpen(false)}
                        />
                    ))}
                </nav>

                {/* Right cluster — search trigger + health + user menu */}
                <div className="ml-auto flex items-center gap-2 sm:gap-3">
                    <button
                        type="button"
                        onClick={() => setPaletteOpen(true)}
                        className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-ux-border-1 text-[12px] text-ux-text-2 hover:text-ux-text-1 hover:bg-ux-bg-2 transition-colors"
                        data-testid="header-search-button"
                        title="Open command palette"
                    >
                        <Search className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Search</span>
                        <kbd
                            className="text-[10px] font-mono text-ux-text-3 border border-ux-border-1 rounded px-1"
                            data-testid="header-search-hotkey"
                        >
                            {platformCmd}
                        </kbd>
                    </button>
                    <HealthBadge data={hb} />
                    <span
                        className="hidden sm:inline text-[10px] font-mono text-ux-text-3 px-1.5 py-0.5 rounded border border-ux-border-1"
                        data-testid="header-version"
                    >
                        {APP_VERSION}
                    </span>
                    <UserCluster user={user} onLogout={handleLogout} />
                </div>
            </header>

            {/* Breadcrumbs — sits below the sticky header on every page. */}
            <Breadcrumbs />

            {/* Main content — rendered by the route. */}
            <main
                className="flex-1 px-4 sm:px-6 lg:px-8 py-6 bg-ux-bg-0 min-w-0 relative overflow-x-hidden"
                data-testid="appshell-main"
            >
                {/* Subtle grid texture. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage:
                            "linear-gradient(var(--ux-border-1) 1px, transparent 1px), linear-gradient(90deg, var(--ux-border-1) 1px, transparent 1px)",
                        backgroundSize: "24px 24px",
                        backgroundAttachment: "fixed",
                    }}
                />
                <div className="relative max-w-[1400px] mx-auto">
                    <Outlet />
                </div>
            </main>

            {/* Mobile drawer. */}
            <MobileDrawer
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                groups={visibleGroups}
                onNavigate={() => setDrawerOpen(false)}
            />

            {/* Command palette overlay. */}
            <CommandPalette
                open={paletteOpen}
                onOpenChange={setPaletteOpen}
                username={user?.username}
            />
        </div>
    );
}
